import { Component, OnInit, input, output, inject, effect, computed } from '@angular/core';
import { FormGroup, FormBuilder, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { isObject } from '../function';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

@Component({
  imports: [CommonModule, MatSelectModule, FormsModule, ReactiveFormsModule, NgSelectModule, MatIconModule],
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  standalone: true,
})
export class SelectComponent implements OnInit {
  private formBuilder = inject(FormBuilder);

  public formField: AbstractControl;
  group = input<FormGroup>();
  isReadOnly = input<boolean>();
  disable = input<boolean>();
  name = input<string>();
  class = input<string>();
  classText = input<string>();
  closeOnSelect = input<boolean>();
  hideSelected = input<boolean>();
  label = input<string>();
  options = input<any[]>([]);
  placeholder = input<any>();
  labelKey = input<string>();
  valueKey = input<string>();
  required = input<boolean>(false);
  multiple = input<boolean>();
  filter = input<boolean>();
  isWhole = input<boolean>(false);
  clearable = input<boolean>();
  isFormSubmitted = input<boolean>();
  modelValue = input<string>();
  isEdit = input<boolean>(true);
  additionalLabelKey = input<string>();
  isErrorOnChange = input<boolean>(false);

  public modelValueChange = output<any>();
  public selectionChange = output<any>();
  public remove = output<any>();
  public modelRemoveValueChange = output<any>();

  get form() {
    return this.group().controls;
  }

  get value() {
    const name = this.name();
    const labelKey = this.labelKey();
    const valueKey = this.valueKey();
    const options = this.options();
    const group = this.group();

    if (!group) return ''; // Guard against undefined group

    const fieldValue = group.value[name];
    if (fieldValue) {
      if (isObject(fieldValue)) {
        return fieldValue[labelKey];
      } else {
        if (options && isObject(options[0])) {
          const selectedOpt = options.filter(option => option[valueKey] === fieldValue)[0];
          return selectedOpt ? selectedOpt[labelKey] : '';
        }
        return fieldValue;
      }
    }
    return '';
  }

  constructor() {
    effect(() => {
      const modelVal = this.modelValue();
      const currentGroup = this.group();
      const currentName = this.name();

      // Similar to ngOnChanges check for modelValue
      if (modelVal !== undefined && currentGroup && currentName) {
        currentGroup.patchValue({ [currentName]: modelVal });
      }
    });
  }

  ngOnInit() {
    // We need to handle the case where group might not be passed, 
    // although input signal is read-only so we can't assign to it.
    // However, if the parent doesn't pass it, it's undefined. 
    // The original code created a group if missing.
    // With data-bound signals we can't easily mutate the input.
    // Assuming for now the pattern is strict or we handle it via a local property if needed.
    // BUT looking at original code: `if (!this.group) { this.group = ... }`
    // This implies we might need a local variable or just reliance on the parent passing it properly.
    // Given this is a shared component, let's assume valid usage or use a local var if we MUST create it.
    // Ideally refactor to avoid mutating inputs. 

    // For this refactor, I will treat `group` as required or handled by parent. 
    // If we truly need to create one, we'd need a woven signal or a separate property.
    // Let's rely on `group` being present for now or deferring valid access.

    if (this.group() && this.name()) {
      this.formField = this.group().controls[this.name()];
    }
  }

  onChange(value: any) {
    if (value) {
      const selectedValue = value.value !== undefined ? value.value : value;
      this.modelValueChange.emit(this.isWhole() ? selectedValue : (selectedValue && this.valueKey() ? selectedValue[this.valueKey()] : selectedValue));
      this.selectionChange.emit(value);

    } else {
      this.modelValueChange.emit(null);
      this.selectionChange.emit(null);
    }
  }

  onRemove(value: any) {
    if (value) {
      this.modelRemoveValueChange.emit(this.isWhole() ? value : value[this.valueKey()]);
      this.remove.emit(value);
    } else {
      this.modelRemoveValueChange.emit(null);
      this.remove.emit(null);
    }
  }
  clearSelection() {
    if (this.formField) {
      this.formField.setValue('');
      this.formField.markAsTouched();
      this.modelValueChange.emit(null);
      this.selectionChange.emit(null);
    }
  }

}